import { QueryClientProvider, focusManager } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { App as CapacitorApp } from '@capacitor/app';
import { useEffect, type StrictMode } from 'react';
import { queryClient } from '@/lib/query-client';
import { isNative } from '@/lib/native-auth';
import { useUIStore } from '@/stores/ui-store';
import { router } from './router';

function ThemeBootstrap() {
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  // APK não tem toggle na UI mobile → segue o tema do sistema (device, claro/escuro).
  // Desktop mantém o toggle manual (Topbar).
  useEffect(() => {
    if (!isNative()) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setTheme(mq.matches ? 'dark' : 'light');
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [setTheme]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  // APK: o WebView não dispara focus/visibilitychange confiável → liga o
  // focusManager do TanStack ao ciclo de vida do app. Ao voltar pro foreground,
  // as queries (chat/lista) refazem fetch na hora.
  useEffect(() => {
    if (!isNative()) return undefined;
    const p = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      focusManager.setFocused(isActive);
    });
    return () => {
      void p.then((h) => h.remove());
    };
  }, []);
  return null;
}

export function AppProviders(): ReturnType<typeof StrictMode> {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeBootstrap />
      <RouterProvider router={router} />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
    </QueryClientProvider>
  );
}
